import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import {
  Button,
  Card,
  EmptyState,
  FilterBar,
  FilterSelect,
  InlineStats,
  Pagination,
  StatusPill,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Tr
} from '../../components/admin/ui';
import { useToast } from '../../components/admin/overlays';
import { IconCases, IconRefresh } from '../../components/admin/icons';
import {
  caseStatusLabels,
  caseStatusTone,
  billedAsLabels,
  billedAsTone,
  createdByLabels,
  inferBilledAs,
  createdByTone,
  emailStatusLabels,
  emailStatusTone,
  formatDate,
  formatNumber,
  issueTypeLabels,
  paymentStatusTone
} from '../../components/admin/labels';

const statusOptions = [
  'submitted',
  'evidence_prepared',
  'merchant_reviewed',
  'merchant_responded',
  'closed',
  'no_merchant_response',
  'system_error'
];

const issueTypeOptions = [
  'not_arrived',
  'delivered_not_received',
  'damaged',
  'missing_items',
  'unsafe_place'
];

const createdByOptions = ['customer', 'merchant'];

const emptyFilters = {
  status: '',
  issueType: '',
  courier: '',
  merchant: '',
  createdBy: '',
  page: 1,
  limit: 20
};

/**
 * Case list. Opening a case navigates to its own page, where the full detail,
 * status control and notes live — a modal was too small for all of it.
 */
const CaseManagement = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [cases, setCases] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(emptyFilters);
  const [merchants, setMerchants] = useState([]);
  const [couriers, setCouriers] = useState([]);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.issueType) params.append('issueType', filters.issueType);
      if (filters.courier) params.append('courier', filters.courier);
      if (filters.merchant) params.append('merchant', filters.merchant);
      if (filters.createdBy) params.append('createdBy', filters.createdBy);
      params.append('page', filters.page);
      params.append('limit', filters.limit);

      const response = await api.get(`/api/cases?${params}`);
      setCases(response.data.cases || []);
      setMeta({
        total: response.data.total || 0,
        totalPages: response.data.totalPages || 1
      });
    } catch (error) {
      toast('Could not load cases.', { tone: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  // Names for the merchant and courier filters — lightweight list fetches,
  // independent of the cases request, so they don't need to be re-run on
  // every filter change.
  useEffect(() => {
    api
      .get('/api/admin/merchants')
      .then(({ data }) => setMerchants(data || []))
      .catch(() => setMerchants([]));
    api
      .get('/api/admin/couriers')
      .then(({ data }) => setCouriers(data || []))
      .catch(() => setCouriers([]));
  }, []);

  const activeFilterCount = ['status', 'issueType', 'courier', 'merchant', 'createdBy'].filter(
    (key) => filters[key]
  ).length;
  const setFilter = (patch) => setFilters({ ...filters, ...patch, page: 1 });

  return (
    // One card, not a filter card above a table card: the old pair opened
    // with roughly a screen of chrome before a single case was visible.
    <Card>
      <FilterBar
        actions={
          <>
            <InlineStats items={[{ label: 'Cases', value: formatNumber(meta.total) }]} />
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setFilters(emptyFilters)}>
                Clear
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={fetchCases} loading={loading}>
              <IconRefresh size={15} />
              Refresh
            </Button>
          </>
        }
      >
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(e) => setFilter({ status: e.target.value })}
        >
          <option value="">All</option>
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {caseStatusLabels[status]}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          label="Issue"
          value={filters.issueType}
          onChange={(e) => setFilter({ issueType: e.target.value })}
        >
          <option value="">All</option>
          {issueTypeOptions.map((type) => (
            <option key={type} value={type}>
              {issueTypeLabels[type]}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          label="Courier"
          value={filters.courier}
          onChange={(e) => setFilter({ courier: e.target.value })}
        >
          <option value="">All</option>
          {couriers.map((c) => (
            <option key={c._id} value={c.name}>
              {c.name}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          label="Merchant"
          value={filters.merchant}
          onChange={(e) => setFilter({ merchant: e.target.value })}
        >
          <option value="">All</option>
          {merchants.map((m) => (
            <option key={m._id} value={m._id}>
              {m.name}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          label="Raised by"
          value={filters.createdBy}
          onChange={(e) => setFilter({ createdBy: e.target.value })}
        >
          <option value="">Anyone</option>
          {createdByOptions.map((value) => (
            <option key={value} value={value}>
              {createdByLabels[value]}
            </option>
          ))}
        </FilterSelect>
      </FilterBar>

      {loading ? (
        <TableSkeleton rows={8} cols={11} />
      ) : cases.length === 0 ? (
        <EmptyState
          icon={IconCases}
          title={activeFilterCount ? 'No cases match these filters' : 'No cases yet'}
          description={
            activeFilterCount
              ? 'Try clearing one or more filters to widen the search.'
              : 'Cases appear here as soon as customers report delivery issues.'
          }
          action={
            activeFilterCount ? (
              <Button variant="secondary" size="sm" onClick={() => setFilters(emptyFilters)}>
                Clear filters
              </Button>
            ) : null
          }
        />
      ) : (
        <TableWrap>
          <thead>
            <tr>
              <Th>Case</Th>
              <Th>Merchant</Th>
              <Th>Customer</Th>
              <Th>Created by</Th>
              <Th>Billed as</Th>
              <Th>Issue</Th>
              <Th>Courier</Th>
              <Th>Email</Th>
              <Th>SMS</Th>
              <Th>Status</Th>
              <Th>Submitted</Th>
            </tr>
          </thead>
          <tbody>
            {cases.map((caseItem) => (
              <Tr key={caseItem._id}>
                <Td>
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/cases/${caseItem.caseId}`)}
                    className="whitespace-nowrap rounded font-600 text-brand-700 transition-colors hover:text-brand-800 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                  >
                    {caseItem.caseId}
                  </button>
                </Td>
                <Td>
                  {caseItem.merchant?.merchantId ? (
                    <>
                      <div className="font-medium text-ink-900">
                        {caseItem.merchant.merchantId.name}
                      </div>
                      <div className="text-xs text-ink-500">{caseItem.merchant.merchantId.email}</div>
                    </>
                  ) : (
                    <span className="text-ink-500">—</span>
                  )}
                </Td>
                <Td>
                  <div className="font-medium text-ink-900">{caseItem.customer?.name}</div>
                  <div className="text-xs text-ink-500">{caseItem.customer?.email}</div>
                </Td>
                <Td>
                  <StatusPill tone={createdByTone[caseItem.createdBy] || 'neutral'}>
                    {createdByLabels[caseItem.createdBy] || 'Customer'}
                  </StatusPill>
                </Td>
                <Td>
                  {(() => {
                    const billedAs = inferBilledAs(caseItem);
                    if (billedAs) {
                      return (
                        <StatusPill tone={billedAsTone[billedAs] || 'neutral'}>
                          {billedAsLabels[billedAs] || billedAs}
                        </StatusPill>
                      );
                    }
                    return caseItem.billingCountedAt ? (
                      <span className="text-ink-500">Billed (plan unknown)</span>
                    ) : (
                      <span className="text-ink-500">Not yet billed</span>
                    );
                  })()}
                </Td>
                <Td className="max-w-[14rem]">
                  <span className="block truncate">
                    {issueTypeLabels[caseItem.issueType] || caseItem.issueType}
                  </span>
                </Td>
                <Td>{caseItem.deliveryDetails?.courier || '—'}</Td>
                <Td>
                  {caseItem.emailStatus?.status ? (
                    <StatusPill tone={emailStatusTone[caseItem.emailStatus.status] || 'neutral'}>
                      {emailStatusLabels[caseItem.emailStatus.status] || caseItem.emailStatus.status}
                    </StatusPill>
                  ) : (
                    <span className="text-ink-500">—</span>
                  )}
                </Td>
                <Td>
                  {caseItem.smsOptIn?.optedIn ? (
                    <StatusPill tone={paymentStatusTone[caseItem.smsOptIn.paymentStatus] || 'neutral'}>
                      {caseItem.smsOptIn.paymentStatus || 'opted in'}
                    </StatusPill>
                  ) : (
                    <span className="text-ink-500">—</span>
                  )}
                </Td>
                <Td>
                  <StatusPill tone={caseStatusTone[caseItem.status] || 'neutral'}>
                    {caseStatusLabels[caseItem.status] || caseItem.status}
                  </StatusPill>
                </Td>
                <Td className="whitespace-nowrap text-ink-500">{formatDate(caseItem.submittedAt)}</Td>
              </Tr>
            ))}
          </tbody>
        </TableWrap>
      )}

      {!loading && (
        <Pagination
          page={filters.page}
          totalPages={meta.totalPages}
          total={meta.total}
          perPage={filters.limit}
          shown={cases.length}
          onPage={(next) => setFilters({ ...filters, page: next })}
        />
      )}
    </Card>
  );
};

export default CaseManagement;
